/**
 * The small amount of linear algebra a RoomPlan transform needs.
 *
 * Kept separate and tested on its own because an index slip here — reading the
 * translation from the wrong matrix elements, or taking a surface's thickness as
 * one of its area terms — is silent. It produces a number, the number is
 * plausible, and it lands in a document somebody signs.
 */

import type { CapturedSurface } from "./roomplan.js";

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const ORIGIN: Vec3 = { x: 0, y: 0, z: 0 };

/**
 * A 4x4 column-major matrix, flattened. Anything shorter is not usable.
 *
 * A type predicate, so a caller that has checked can use the matrix without a
 * non-null assertion — and a caller that has NOT checked cannot, which is the
 * point: an element with no transform has no position and must be skipped, not
 * placed at the origin.
 */
export function isUsableTransform(
  transform: readonly number[] | undefined,
): transform is readonly number[] {
  return (
    Array.isArray(transform) &&
    transform.length >= 16 &&
    transform.every((n) => typeof n === "number" && Number.isFinite(n))
  );
}

function at(transform: readonly number[], index: number): number {
  const value = transform[index];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/** Column 3 of the matrix: elements 12, 13, 14. The surface's centre. */
export function translationOf(transform: readonly number[]): Vec3 {
  return { x: at(transform, 12), y: at(transform, 13), z: at(transform, 14) };
}

/** Column 0: the surface's local X axis, which runs along its width. */
export function widthAxisOf(transform: readonly number[]): Vec3 {
  return { x: at(transform, 0), y: at(transform, 1), z: at(transform, 2) };
}

/** Column 1: the surface's local Y axis, which runs along its height. */
export function heightAxisOf(transform: readonly number[]): Vec3 {
  return { x: at(transform, 4), y: at(transform, 5), z: at(transform, 6) };
}

/** Column 2: the surface's local Z axis, which is its outward normal. */
export function normalOf(transform: readonly number[]): Vec3 {
  return { x: at(transform, 8), y: at(transform, 9), z: at(transform, 10) };
}

export function subtract(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

export function magnitude(v: Vec3): number {
  return Math.sqrt(dot(v, v));
}

export function distance(a: Vec3, b: Vec3): number {
  return magnitude(subtract(a, b));
}

/** Width and height of a surface in its own plane: dimensions x and y. */
export function widthOf(surface: CapturedSurface): number {
  const w = surface.dimensions?.[0];
  return typeof w === "number" && Number.isFinite(w) ? w : 0;
}

export function heightOf(surface: CapturedSurface): number {
  const h = surface.dimensions?.[1];
  return typeof h === "number" && Number.isFinite(h) ? h : 0;
}

function corner(point: readonly number[]): Vec3 {
  return { x: point[0] ?? 0, y: point[1] ?? 0, z: point[2] ?? 0 };
}

/**
 * Area of a planar polygon given its corners in 3D, by Newell's method:
 * the summed cross products of consecutive corners give twice the area vector,
 * whose magnitude is twice the area.
 *
 * The 2D shoelace formula would be simpler but needs the polygon flattened onto
 * a known plane first, and RoomPlan's polygon corners arrive in the surface's own
 * local frame — which is horizontal for a floor and vertical for a wall. Newell
 * needs no such assumption and gives the same answer whatever plane the polygon
 * lies in, so there is no orientation to get wrong.
 */
export function polygonArea(corners: readonly (readonly number[])[]): number {
  if (corners.length < 3) return 0;
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < corners.length; i++) {
    const a = corner(corners[i] ?? []);
    const b = corner(corners[(i + 1) % corners.length] ?? []);
    const c = cross(a, b);
    nx += c.x;
    ny += c.y;
    nz += c.z;
  }
  return magnitude({ x: nx, y: ny, z: nz }) / 2;
}

/**
 * The area of a captured surface.
 *
 * Polygon corners win when present, because that is the only reason RoomPlan
 * emits them: the surface is not a rectangle and `dimensions` is its bounding
 * box. Taking width x height for an L-shaped room would overstate its floor —
 * and therefore its volume, its ventilation loss and its heat pump.
 */
export function surfaceArea(surface: CapturedSurface): number {
  const corners = surface.polygonCorners;
  if (Array.isArray(corners) && corners.length >= 3) {
    const area = polygonArea(corners);
    if (area > 0) return area;
  }
  return widthOf(surface) * heightOf(surface);
}

/**
 * Rounds a metric quantity to six decimal places: a square millimetre of area,
 * or a thousandth of a millimetre of distance.
 *
 * Not for accuracy — a millimetre is three orders of magnitude below what a LiDAR
 * scan resolves. It is so that binary floating point noise (4.2 x 2.4 evaluating
 * to 10.079999999999998) cannot make an idempotent re-upload of the same capture
 * look like a changed value to anything comparing payloads.
 */
export function roundArea(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 1e6) / 1e6;
}

/** Median, used where one badly-scanned member should not move the answer. */
export function median(values: readonly number[]): number | undefined {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  if (sorted.length % 2 === 1) return sorted[mid] ?? undefined;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}
